import { useEditCampaignSipTrunk } from '@features/Admin/Campaign/hooks/useEditCampaignSipTrunk';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { SipSettingsForm } from '@shared/components/forms/SipSettingsForm';
import {
  CampaignDto,
  CampaignSipTrunkCreateRequestDto,
  useCampaignSipTrunkControllerGetSipTrunkQuery,
} from '@shared/services/apiService';
import { SipTruncFieldsError } from '@shared/types/admin/campaign';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash';
import { mapCampaignSipToDefaultSip } from '../utils';
import { IconPhoneCheck } from '@tabler/icons-react';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { CampaignSectionSaveActions } from '../EditCampaignSectionActions';

interface SipTrunkTabProps {
  readOnly?: boolean;
  campaign: CampaignDto;
  onDirtyChange?: (isDirty: boolean) => void;
  onActionsChange?: (actions: SipTrunkActions | null) => void;
}

const normalizeSipSettings = (
  settings: CampaignSipTrunkCreateRequestDto | null,
  campaignType: CampaignDto['type'],
) => ({
  type: settings?.type ?? campaignType,
  numbers: settings?.numbers ?? [],
  address: settings?.address || '',
  transport: settings?.transport || '',
  allowedAddresses: settings?.allowedAddresses ?? [],
  authUsername: settings?.authUsername || '',
  authPassword: settings?.authPassword || '',
  fromHost: settings?.fromHost || '',
  dialCountry: settings?.dialCountry || '',
  dialPrefix: settings?.dialPrefix || '',
});

export type SipTrunkActions = {
  save: () => Promise<boolean>;
  discard: () => void;
  isBusy: boolean;
};

type PendingPhoneNumber = {
  value: string;
  isValid: boolean;
  isDuplicate: boolean;
};

type PendingAllowedAddress = PendingPhoneNumber;

const SipTrunkTab: React.FC<SipTrunkTabProps> = ({
  campaign,
  readOnly = false,
  onDirtyChange,
  onActionsChange,
}: SipTrunkTabProps) => {
  const theme = useTheme();
  const currentUserRole = useAppSelector((state: RootState) => state.auth.authenticatedUser?.role);
  const isReadOnly = readOnly || currentUserRole === UserRolesEnum.VIEWER;

  const [isSipValid, setSipValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [actualSipTrunkSetting, setActualSipTrunkSetting] =
    useState<CampaignSipTrunkCreateRequestDto | null>(null);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState<PendingPhoneNumber>({
    value: '',
    isValid: true,
    isDuplicate: false,
  });
  const [pendingAllowedAddress, setPendingAllowedAddress] = useState<PendingAllowedAddress>({
    value: '',
    isValid: true,
    isDuplicate: false,
  });
  const [phoneNumberInputResetKey, setPhoneNumberInputResetKey] = useState(0);
  const [allowedAddressInputResetKey, setAllowedAddressInputResetKey] = useState(0);
  const [, setSipTrunkCreateFieldError] = useState<SipTruncFieldsError>({
    type: [],
    authUsername: [],
    authPassword: [],
    numbers: [],
    address: [],
    transport: [],
    editErrorMessage: '',
  });

  const { data: campaignSipTrunk, refetch: refetchCampaignSip } =
    useCampaignSipTrunkControllerGetSipTrunkQuery({
      id: campaign.id,
    });

  const savedSipTrunkSetting = useMemo(
    () => (campaignSipTrunk ? mapCampaignSipToDefaultSip(campaignSipTrunk) : null),
    [campaignSipTrunk],
  );

  const notify = () => {
    toast.success(CustomSuccessToast, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
  };

  const { onSubmit, formMethods } = useEditCampaignSipTrunk({
    setSipTrunkCreateFieldError,
    onSuccess: notify,
  });
  const { reset, setValue } = formMethods;

  const resetSipTrunkForm = useCallback(
    (settings: CampaignSipTrunkCreateRequestDto | null) => {
      reset({
        campaignId: campaign.id,
        address: settings?.address || undefined,
        authPassword: settings?.authPassword || undefined,
        authUsername: settings?.authUsername || undefined,
        numbers: settings?.numbers ?? [],
        transport: settings?.transport || undefined,
        allowedAddresses: settings?.allowedAddresses || undefined,
        fromHost: settings?.fromHost || undefined,
        dialCountry: settings?.dialCountry || undefined,
        dialPrefix: settings?.dialPrefix || undefined,
        type: campaign.type,
      });
    },
    [campaign.id, campaign.type, reset],
  );

  const handleSipSettingsChange = (settings: CampaignSipTrunkCreateRequestDto | null) => {
    setActualSipTrunkSetting(settings);

    setValue('type', settings?.type ?? campaign.type);
    setValue('numbers', settings?.numbers ?? []);
    setValue('address', settings?.address ?? undefined);
    setValue('transport', settings?.transport);
    setValue('allowedAddresses', settings?.allowedAddresses ?? undefined);
    setValue('authUsername', settings?.authUsername ?? undefined);
    setValue('authPassword', settings?.authPassword ?? undefined);
    setValue('fromHost', settings?.fromHost ?? undefined);
    setValue('dialCountry', settings?.dialCountry ?? undefined);
    setValue('dialPrefix', settings?.dialPrefix ?? undefined);
  };

  const handlePhoneNumberDraftChange = useCallback(
    (value: string, isValid: boolean, isDuplicate: boolean) => {
      setPendingPhoneNumber({ value, isValid, isDuplicate });
    },
    [],
  );

  const handleAllowedAddressDraftChange = useCallback(
    (value: string, isValid: boolean, isDuplicate: boolean) => {
      setPendingAllowedAddress({ value, isValid, isDuplicate });
    },
    [],
  );

  const commitPendingSipInputs = useCallback(() => {
    const pendingNumber = pendingPhoneNumber.value.trim();
    const pendingAddress = pendingAllowedAddress.value.trim();

    if (
      (pendingNumber && (!pendingPhoneNumber.isValid || pendingPhoneNumber.isDuplicate)) ||
      (pendingAddress && (!pendingAllowedAddress.isValid || pendingAllowedAddress.isDuplicate))
    ) {
      return false;
    }

    if (!pendingNumber && !pendingAddress) {
      return true;
    }

    const nextSettings: CampaignSipTrunkCreateRequestDto = {
      ...(actualSipTrunkSetting ?? { type: campaign.type, numbers: [] }),
      type: actualSipTrunkSetting?.type ?? campaign.type,
      ...(pendingNumber && {
        numbers: [...(actualSipTrunkSetting?.numbers ?? []), pendingNumber],
      }),
      ...(pendingAddress && {
        allowedAddresses: [...(actualSipTrunkSetting?.allowedAddresses ?? []), pendingAddress],
      }),
    };

    setActualSipTrunkSetting(nextSettings);
    setValue('type', nextSettings.type);
    setValue('numbers', nextSettings.numbers);
    setValue('allowedAddresses', nextSettings.allowedAddresses ?? undefined);
    setPendingPhoneNumber({ value: '', isValid: true, isDuplicate: false });
    setPendingAllowedAddress({ value: '', isValid: true, isDuplicate: false });
    setPhoneNumberInputResetKey((key) => key + 1);
    setAllowedAddressInputResetKey((key) => key + 1);

    return true;
  }, [actualSipTrunkSetting, campaign.type, pendingAllowedAddress, pendingPhoneNumber, setValue]);

  const submitSipTrunk = useCallback(async (): Promise<boolean> => {
    if (!commitPendingSipInputs()) return false;

    setIsSaving(true);

    try {
      const isSaved = await onSubmit();
      if (isSaved) await refetchCampaignSip();
      return isSaved;
    } finally {
      setIsSaving(false);
    }
  }, [commitPendingSipInputs, onSubmit, refetchCampaignSip]);

  const handleSipTrunkCancel = useCallback(() => {
    setPendingPhoneNumber({ value: '', isValid: true, isDuplicate: false });
    setPendingAllowedAddress({ value: '', isValid: true, isDuplicate: false });
    setPhoneNumberInputResetKey((key) => key + 1);
    setAllowedAddressInputResetKey((key) => key + 1);
    setActualSipTrunkSetting(savedSipTrunkSetting);
    resetSipTrunkForm(savedSipTrunkSetting);
  }, [resetSipTrunkForm, savedSipTrunkSetting]);

  useEffect(() => {
    const nextSettings = campaignSipTrunk ? mapCampaignSipToDefaultSip(campaignSipTrunk) : null;

    setPendingPhoneNumber({ value: '', isValid: true, isDuplicate: false });
    setPendingAllowedAddress({ value: '', isValid: true, isDuplicate: false });
    setPhoneNumberInputResetKey((key) => key + 1);
    setAllowedAddressInputResetKey((key) => key + 1);
    setActualSipTrunkSetting(nextSettings);
    resetSipTrunkForm(nextSettings);
  }, [campaignSipTrunk, resetSipTrunkForm]);

  const isDirty =
    !isEqual(
      normalizeSipSettings(actualSipTrunkSetting, campaign.type),
      normalizeSipSettings(savedSipTrunkSetting, campaign.type),
    ) ||
    Boolean(pendingPhoneNumber.value.trim()) ||
    Boolean(pendingAllowedAddress.value.trim());

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [
    actualSipTrunkSetting,
    campaign.id,
    campaign.type,
    isDirty,
    onDirtyChange,
    pendingAllowedAddress,
    pendingPhoneNumber,
    savedSipTrunkSetting,
  ]);

  useEffect(() => {
    onActionsChange?.({
      save: submitSipTrunk,
      discard: handleSipTrunkCancel,
      isBusy: isSaving,
    });

    return () => onActionsChange?.(null);
  }, [handleSipTrunkCancel, isSaving, onActionsChange, submitSipTrunk]);

  return (
    <Grid
      container
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
        flexDirection: 'column',
        width: '100%',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          marginBottom: '20px',
          width: '100%',
          minHeight: 36,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minHeight: 36,
            'svg path': {
              stroke: theme.palette.getContrastText(theme.palette.background.default),
            },
          }}>
          <IconPhoneCheck size={22} stroke={1.5} />
          <Typography
            component="span"
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '36px',
              display: 'inline-block',
            }}>
            {t('campaignPage.editCampaign.sipTrunk')}
          </Typography>
        </Box>
        <CampaignSectionSaveActions
          isReadOnly={isReadOnly}
          isDirty={isDirty}
          isSaving={isSaving}
          isSaveDisabled={isReadOnly || !isDirty || isSaving || !isSipValid}
          onSave={() => void submitSipTrunk()}
          onCancel={handleSipTrunkCancel}
        />
      </Box>
      <Box sx={{ maxWidth: 720, width: '100%' }}>
        <SipSettingsForm
          settings={actualSipTrunkSetting}
          onChange={handleSipSettingsChange}
          onPhoneNumberDraftChange={handlePhoneNumberDraftChange}
          onAllowedAddressDraftChange={handleAllowedAddressDraftChange}
          onValidationChange={setSipValid}
          phoneNumberInputResetKey={phoneNumberInputResetKey}
          allowedAddressInputResetKey={allowedAddressInputResetKey}
          type={campaign.type}
          disabled={isReadOnly || isSaving}
          hideTitle
        />
      </Box>
    </Grid>
  );
};

export default SipTrunkTab;
